import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Stack,
    Grid,
    MenuItem,
    CircularProgress,
    IconButton,
    Card,
    InputAdornment
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { setDoc, doc, getDocs, collection, updateDoc, deleteDoc, query, orderBy, where, getDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const Course = () => {
    const uid = auth.currentUser?.uid;

    const [schoolId, setSchoolId] = useState(null);
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        year: '',
        branches: '',
        college_type: '',
        compulsary_lang: '',
        sec_language_first: '',
        sec_language_second: '',
        sec_language_third: '',
        sec_language_four: '',
        sec_language_five: '',
        sec_language_six: '',
        sec_language_seven: '',
        no_of_seats: '',
        df_std_fess: '',
        sequence: '',
        pattern: '',
        even_odd: '',
        degree: '',
        sufix: '',
        status: 'Active',
        description: '',
        schoolId: ''
    });
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [branchOptions, setBranchOptions] = useState([]);
    const [collegeTypes, setCollegeTypes] = useState([]);

    // Fetch schoolId from users collection
    const fetchSchoolId = async () => {
        try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                const id = userDoc.data().schoolId;
                setSchoolId(id);
                setFormData(prev => ({ ...prev, schoolId: id }));
            } else {
                toast.error("User data not found");
            }
        } catch (err) {
            console.error("Error fetching user data:", err);
            toast.error("Failed to get schoolId");
        }
    };

    // Fetch branch and college options
    const fetchOptions = async (schoolId) => {
        if (!schoolId) return;
        const branchesQuery = query(collection(db, 'branches'), where('schoolId', '==', schoolId));
        const collegesQuery = query(collection(db, 'collegetypes'), where('schoolId', '==', schoolId));

        const branchesSnap = await getDocs(branchesQuery);
        const collegesSnap = await getDocs(collegesQuery);

        setBranchOptions(branchesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setCollegeTypes(collegesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    // Fetch courses for the school
    const fetchCourses = async (schoolId) => {
        if (!schoolId) return;
        try {
            const q = query(collection(db, 'courses'), where('schoolId', '==', schoolId), orderBy('sequence', 'asc'));
            const snapshot = await getDocs(q);
            setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error(err);
            // toast.error("Failed to fetch courses");
        }
    };

    useEffect(() => {
        if (uid) fetchSchoolId();
    }, [uid]);

    useEffect(() => {
        if (schoolId) {
            fetchOptions(schoolId);
            fetchCourses(schoolId);
        }
    }, [schoolId]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setFormData(prev => ({ ...prev, code: '', name: '', year: '', branches: '', college_type: '', compulsary_lang: '', sec_language_first: '', sec_language_second: '', sec_language_third: '', sec_language_four: '', sec_language_five: '', sec_language_six: '', sec_language_seven: '', no_of_seats: '', df_std_fess: '', pattern: '', even_odd: '', degree: '', sufix: '', description: '', status: 'Active' }));
        setEditMode(false);
        setEditId(null);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async () => {
        const { code, name, year, branches, college_type } = formData;

        if (!name || !year) {
            toast.error("Please fill all required fields");
            return;
        }

        setLoading(true);

        try {

            const payload = {
                ...formData,
                sequence: Number(formData.sequence), // 🔴 force number
            };

            if (editMode && editId) {

                await updateDoc(doc(db, "courses", editId), {
                    ...payload,
                    updatedAt: new Date()
                });

                toast.success("Course updated successfully!");

            } else {

                const id = uuidv4();

                await setDoc(doc(db, "courses", id), {
                    ...payload,
                    createdAt: new Date(),
                    schoolId
                });

                toast.success("Course added successfully!");
            }

            fetchCourses(schoolId);
            handleClose();

        } catch (err) {

            console.error(err);
            toast.error("Error: " + err.message);

        }

        setLoading(false);
    };
    const filteredCourses = courses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedCourses = filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleEdit = (course) => {
        setEditMode(true);
        setEditId(course.id);
        setFormData({ ...course });
        setOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this Course?')) return;
        try {
            await deleteDoc(doc(db, 'courses', id));
            setCourses(prev => prev.filter(c => c.id !== id));
            toast.success('Course deleted successfully');
        } catch (err) {
            console.error(err);
            toast.error('Delete failed');
        }
    };

    return (
        <Box sx={{ mb: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant="contained" onClick={handleOpen}>Add Course / Class</Button>
                <Typography variant="h5" fontWeight="bold">Course / Class List</Typography>
            </Stack>

            <Card>
                <Box sx={{ p: 1 }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search by name or code"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Course / Class Name</TableCell>
                                <TableCell>Course / Class Code</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell>Branch</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCourses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">No entries yet</TableCell>
                                </TableRow>
                            ) : (
                                paginatedCourses.map((course, index) => (
                                    <TableRow key={course.id}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Button variant="contained" color="primary" size="small" onClick={() => handleEdit(course)}><EditIcon /></Button>
                                                <Button variant="contained" color="error" size="small" onClick={() => handleDelete(course.id)}><DeleteIcon /></Button>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{course.name}</TableCell>
                                        <TableCell>{course.code}</TableCell>
                                        <TableCell>{course.year}</TableCell>
                                        <TableCell>{course.branches}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    backgroundColor: course.status === 'Active' ? 'green' : 'red',
                                                    '&:hover': {
                                                        backgroundColor: course.status === 'Active' ? 'darkgreen' : 'darkred'
                                                    }
                                                }}
                                            >{course.status}</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20]}
                        component="div"
                        count={filteredCourses.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Card>

            <Dialog open={open} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {editMode ? 'Edit Course / Class' : 'Add Course / Class'}
                    <IconButton onClick={handleClose}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <div className="row">
                         <Grid className="col-sm-4 mb-4">
                            <TextField select label="College Type" name="college_type" fullWidth size="small" value={formData.college_type} onChange={handleChange}>
                                <MenuItem>Select</MenuItem>
                                {collegeTypes.map(item => (
                                    <MenuItem key={item.id} value={item.type}>{item.type}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid className="col-sm-4 mb-4">
                            <TextField select label="Branches" name="branches" fullWidth size="small" value={formData.branches} onChange={handleChange}>
                                <MenuItem>Select</MenuItem>
                                {branchOptions.map(item => (
                                    <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {[{ label: 'Course / Class Code', name: 'code' }, { label: 'Course / Class Name', name: 'name' }, { label: 'Year', name: 'year' },
                        { label: 'Sequence', name: 'sequence' }, { label: 'Compulsory Language', name: 'compulsary_lang' }, { label: 'Sec Lang 1', name: 'sec_language_first' }, { label: 'Sec Lang 2', name: 'sec_language_second' }, { label: 'Sec Lang 3', name: 'sec_language_third' }, { label: 'Sec Lang 4', name: 'sec_language_four' }, { label: 'Sec Lang 5', name: 'sec_language_five' }, { label: 'Sec Lang 6', name: 'sec_language_six' }, { label: 'Sec Lang 7', name: 'sec_language_seven' }, { label: 'No. of Seats', name: 'no_of_seats' }, { label: 'Default Std Fees', name: 'df_std_fess' }, { label: 'Pattern', name: 'pattern' }, { label: 'Even/Odd', name: 'even_odd' }, { label: 'Degree', name: 'degree' }, { label: 'Suffix', name: 'sufix' }, { label: 'Description', name: 'description' }].map(field => (
                            <Grid className="col-sm-4 mb-4" key={field.name}>
                                <TextField label={field.label} name={field.name} fullWidth size="small" value={formData[field.name]} onChange={handleChange} />
                            </Grid>
                        ))}
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="error">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Course;